import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Row, RowState } from "./Row";
import { Clue, clue, CluedLetterPair } from "./clue";
import { Keyboard } from "./Keyboard";
import { longPollGameState, fetchGameStateForPuzzle, RemoteGameState } from "./gameStateApi";
import { Puzzle } from "./Game";

interface WatchViewProps {
  puzzleId: string;
  colorBlind: boolean;
  keyboardLayout: string;
  targetUserId?: string;
  watchLabel?: string;
  onStatusChange?: (status: "in_progress" | "won" | "lost") => void;
  puzzle: Puzzle;
  maxGuesses: number;
  boundLookup: Map<string, string[]>;
  children?: ReactNode;
}

export function WatchView({
  puzzleId,
  keyboardLayout,
  targetUserId,
  watchLabel,
  onStatusChange,
  puzzle,
  maxGuesses,
  boundLookup,
  children,
}: WatchViewProps) {
  const [remote, setRemote] = useState<RemoteGameState | null>(null);
  const [error, setError] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    let active = true;

    async function run() {
      if (targetUserId) {
        // Watch a specific user's game directly
        const data = await longPollGameState(targetUserId, puzzleId, null);
        if (!active) return;
        if (data) {
          setRemote(data);
          setError(false);
          await longPollLoop(targetUserId, data.updated_at || null);
        } else {
          setError(true);
        }
        return;
      }

      const initial = await fetchGameStateForPuzzle(puzzleId);
      if (!active) return;
      if (!initial || !initial.user_id) {
        setError(true);
        while (active) {
          await new Promise((r) => setTimeout(r, 3000));
          if (!active) return;
          const retry = await fetchGameStateForPuzzle(puzzleId);
          if (!active) return;
          if (retry && retry.user_id) {
            setRemote(retry);
            setError(false);
            await longPollLoop(retry.user_id, retry.updated_at || null);
            return;
          }
        }
        return;
      }

      setRemote(initial);
      setError(false);
      await longPollLoop(initial.user_id, initial.updated_at || null);
    }

    async function longPollLoop(userId: string, after: string | null) {
      while (active) {
        const controller = new AbortController();
        abortRef.current = controller;
        const data = await longPollGameState(userId, puzzleId, after, controller.signal);
        if (!active) return;
        if (data) {
          setRemote(data);
          after = data.updated_at || null;
        }
      }
    }

    run();

    return () => {
      active = false;
      abortRef.current?.abort();
    };
  }, [puzzleId, targetUserId]);

  useEffect(() => {
    if (onStatusChange && remote) {
      onStatusChange(remote.metadata.status);
    }
  }, [remote?.metadata.status]);

  if (error && !remote) {
    return (
      <>
        <div className="watch-banner-wrapper">
          <div className="watch-banner">Waiting for player to start...</div>
        </div>
        {children}
      </>
    );
  }

  if (!remote) {
    return (
      <>
        <div className="watch-banner-wrapper">
          <div className="watch-banner">Loading...</div>
        </div>
        {children}
      </>
    );
  }

  const gameOver = remote.metadata.status === "won" || remote.metadata.status === "lost";
  const isLive = remote.metadata.status === "in_progress";

  // Build the board from remote state
  const allGuesses = remote.state.guesses;
  const currentGuessStr = remote.state.currentGuess || "          ";
  const cursorPos = remote.state.cursorPosition ?? -1;

  const totalRows = remote.metadata.status === "won"
    ? allGuesses.length
    : Math.max(allGuesses.length + (gameOver ? 0 : 1), maxGuesses);

  let letterInfo = new Map<string, [Clue, number]>();

  const cluedRows = Array(totalRows)
    .fill(undefined)
    .map((_, i) => {
      const guess = [...allGuesses.map((g) => g.word), currentGuessStr][i] ?? "";
      return clue(guess, puzzle.bindings, puzzle.target);
    });

  const tableRows = Array(totalRows)
    .fill(undefined)
    .map((_, i) => {
      const cluedLetters = cluedRows[i];
      const lockedIn = i < allGuesses.length;
      const isEditing = !gameOver && i === allGuesses.length;
      const guess = lockedIn ? allGuesses[i].word : undefined;
      const possibleWords = guess ? (boundLookup.get(guess) ?? []) : [];
      const annotation = possibleWords.length > 0
        ? possibleWords.join(" ")
        : "\u00a0";

      if (lockedIn) {
        for (const { clue: c, letterPair } of cluedLetters) {
          if (c === undefined) break;
          const count = cluedLetters.filter(
            (cl) => cl.letterPair === letterPair && cl.clue && cl.clue > Clue.Absent
          ).length;
          const old = letterInfo.get(letterPair);
          const newCount = old === undefined || old[1] < count ? count : old[1];
          const newClue = old === undefined || old[0] < c ? c : old[0];
          letterInfo.set(letterPair, [newClue, newCount]);
        }
      }

      return (
        <Row
          onClick={() => {}}
          currentGuess={isEditing ? [currentGuessStr, cursorPos] : ["          ", -1]}
          key={i}
          rowState={
            lockedIn
              ? RowState.LockedIn
              : isEditing
              ? RowState.Editing
              : RowState.Pending
          }
          cluedRows={cluedRows.slice(undefined, allGuesses.length)}
          cluedLetters={cluedLetters}
          letterInfo={letterInfo}
          rowNumber={i}
          annotation={annotation}
          rowWidth={puzzle.target.length}
        />
      );
    });

  const bannerText = watchLabel
    ? watchLabel
    : isLive
    ? "Watching Live"
    : gameOver
    ? remote.metadata.status === "won"
      ? "Replay \u2014 Player Won"
      : "Replay \u2014 Player Lost"
    : "Watch Mode";

  return (
    <>
      <div className="watch-banner-wrapper">
        <div className={`watch-banner${isLive ? " watch-banner--live" : ""}`}>
          {isLive && <span className="watch-dot" />}
          {bannerText}
        </div>
      </div>
      <div className={`watch-border${isLive ? " watch-border--live" : ""}`}>
        <table
          className="Game-rows"
          tabIndex={0}
          aria-label="watched game board"
        >
          <tbody>{tableRows}</tbody>
        </table>
      </div>
      {children}
      <Keyboard
        layout={keyboardLayout}
        letterPairStyle="az"
        letterSort="AZ"
        letterInfo={letterInfo}
        onKey={() => {}}
        bindings={puzzle.bindings}
      />
    </>
  );
}
